import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Smile, Undo2 } from "lucide-react";

// A curated set of emojis covering the most-used categories — no external dependency needed.
const EMOJI_SET = [
  "😀", "😂", "😅", "😊", "😍", "😘", "🤩", "🤔",
  "😎", "🥳", "😉", "😏", "🙃", "😴", "🤝", "👋",
  "👍", "👎", "👏", "🙌", "💪", "🙏", "✌️", "🤞",
  "🔥", "✨", "💯", "🎉", "🎊", "🏆", "🥇", "🏏",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🤍", "💔",
  "⭐", "🌟", "💥", "💫", "⚡", "☀️", "🌈", "🍻",
];

/**
 * EmojiPicker — render an icon button that opens a popover with a curated emoji grid.
 *
 * Props:
 *   - onSelect(emoji): called when the user picks an emoji
 *   - asReaction (bool): show a compact trigger styled for the "react" context
 *   - closeOnSelect (bool): close the popover after an emoji is picked (default true for reactions)
 *   - onUnsend(): optional callback — if provided, an "Unsend" button renders to the right
 *   - triggerClassName: override the default trigger sizing/colour entirely
 *   - testid: optional data-testid prefix
 */
export default function EmojiPicker({
  onSelect,
  onUnsend,
  asReaction = false,
  closeOnSelect,
  triggerClassName,
  testid = "emoji-picker",
}) {
  const [open, setOpen] = useState(false);
  const shouldClose = closeOnSelect ?? asReaction;

  const baseClass = asReaction
    ? "inline-flex items-center justify-center shrink-0 h-7 w-7 rounded-full border border-border bg-background text-foreground hover:bg-[hsl(var(--secondary))] hover:text-[hsl(var(--accent))] transition-colors"
    : "inline-flex items-center justify-center shrink-0 h-10 w-10 rounded-full border border-border bg-background text-foreground hover:bg-[hsl(var(--secondary))] hover:text-[hsl(var(--accent))] transition-colors";

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={asReaction ? "Add reaction" : "Add emoji"}
          className={triggerClassName || baseClass}
          data-testid={`${testid}-trigger`}
        >
          <Smile className={asReaction ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[280px] p-2 rounded-xl z-50"
        data-testid={`${testid}-content`}
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-1 pb-1.5">
          {asReaction ? "Pick a reaction" : "Pick an emoji"}
        </div>
        <div className="grid grid-cols-8 gap-1 max-h-[200px] overflow-y-auto">
          {EMOJI_SET.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                if (onSelect) onSelect(e);
                if (shouldClose) setOpen(false);
              }}
              className="h-8 w-8 rounded-md text-xl hover:bg-[hsl(var(--secondary))] active:scale-95 transition-transform"
              data-testid={`${testid}-option-${e}`}
              aria-label={`Insert ${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
    {onUnsend && (
      <button
        type="button"
        onClick={onUnsend}
        aria-label="Unsend message"
        className={
          asReaction
            ? "inline-flex items-center justify-center shrink-0 h-7 w-7 rounded-full border border-border bg-background text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors"
            : "inline-flex items-center justify-center shrink-0 h-10 w-10 rounded-full border border-border bg-background text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors"
        }
        data-testid={`${testid}-unsend`}
      >
        <Undo2 className={asReaction ? "h-3.5 w-3.5" : "h-4 w-4"} />
      </button>
    )}
    </>
  );
}
